import { ChevronDown, Copy, GraduationCap, X } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import {
  NAVIGATION,
  isNavGroup,
  type NavEntry,
  type NavGroup,
  type NavItem,
} from '@/config/navigation';
import {
  PLATFORM_NAVIGATION,
  PLATFORM_ROLES,
  PLATFORM_SUBSCRIPTION_CONFIRMATIONS_HREF,
  PLATFORM_TENANTS_HREF,
} from '@/config/platform-navigation';
import { useNavItemVisibility } from '@/hooks/use-nav-visibility';
import { usePendingRegistrationCount } from '@/hooks/use-pending-registrations';
import { usePendingSubscriptionConfirmationCount } from '@/hooks/use-pending-subscription-confirmations';
import { usePlatformBranding } from '@/hooks/use-platform-branding';
import { cn, tenantPortalUrl } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Alamat Portal Wali Murid lembaga ini, terpampang permanen di kaki sidebar.
 *
 * Ditaruh di sini — bukan hanya di halaman Akun Wali Murid dan Setelan
 * Lembaga — karena inilah satu-satunya hal yang admin butuhkan berulang kali
 * dan tidak bisa ia tebak: setiap wali baru perlu diberi tautannya, dan
 * mencarinya lewat dua klik ke halaman lain setiap kali membuat orang berhenti
 * membagikannya sama sekali.
 */
function PortalLinkFooter({ slug }: { slug: string }) {
  const url = tenantPortalUrl(slug);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Tautan portal wali disalin');
    } catch {
      toast.error('Peramban menolak akses papan klip — salin dari Setelan Lembaga');
    }
  };

  return (
    <div className="shrink-0 border-t border-[var(--color-border)] p-3">
      <p className="px-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">
        Portal Wali Murid
      </p>
      <div className="mt-1 flex items-center gap-1">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 flex-1 truncate rounded-[var(--radius-sm)] px-1 py-0.5 font-mono text-xs text-[var(--color-primary)] hover:underline"
          title={url}
        >
          {slug}.sipaud.com
        </a>
        <button
          type="button"
          onClick={copy}
          aria-label="Salin tautan portal wali"
          className="shrink-0 rounded-[var(--radius-sm)] p-1.5 text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-accent)] hover:text-[var(--color-foreground)]"
        >
          <Copy className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** Satu tautan menu; `nested` untuk yang berada di dalam induk. */
function NavRow({
  item,
  nested = false,
  onNavigate,
  badge,
}: {
  item: NavItem;
  nested?: boolean;
  onNavigate: () => void;
  /** Angka penanda di ujung baris; disembunyikan bila 0 atau tidak diisi. */
  badge?: number;
}) {
  const { pathname } = useLocation();
  const isInActivePaths = item.activePaths?.includes(pathname) ?? false;

  return (
    <NavLink
      to={item.href}
      // `end` hanya untuk beranda ("/" dan "/admin"): tanpanya Dashboard
      // Platform ikut tersorot di setiap halaman /admin/... di bawahnya.
      end={item.href === '/' || item.href === '/admin'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-[var(--radius-md)] py-2 text-sm transition-colors',
          nested ? 'pl-11 pr-3' : 'px-3',
          isActive || isInActivePaths
            ? 'nav-active bg-[var(--color-primary-subtle)] font-medium text-[var(--color-primary-on-subtle)]'
            : 'text-[var(--color-muted-foreground)] hover:bg-[var(--color-accent)] hover:text-[var(--color-foreground)]',
        )
      }
    >
      {/* Anak menu tanpa ikon: ketiganya sejenis, jadi ikon yang sama berulang
          hanya menambah ramai — induknya yang membawa ikon. */}
      {!nested && <item.icon className="size-4 shrink-0" aria-hidden="true" />}
      <span className="truncate">{item.label}</span>

      {/* Angkanya disuarakan lengkap: "3" sendirian tidak memberi tahu pembaca
          layar bahwa itu antrean yang menunggu tindakan. */}
      {badge !== undefined && badge > 0 && (
        <span className="ml-auto flex min-w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-warning-subtle)] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--color-warning-on-subtle)]">
          <span aria-hidden="true">{badge}</span>
          <span className="sr-only">{badge} menunggu persetujuan</span>
        </span>
      )}
    </NavLink>
  );
}

/**
 * Induk yang bisa dibuka-tutup.
 *
 * Terbuka sendiri selama salah satu halaman di dalamnya sedang dibuka, supaya
 * halaman yang sedang dilihat tidak pernah tersembunyi di balik induk yang
 * tertutup. Sesudah dibuka/ditutup sendiri oleh pengguna, pilihannya yang
 * berlaku sampai ia berpindah halaman.
 */
function NavGroupRow({ group, onNavigate }: { group: NavGroup; onNavigate: () => void }) {
  const { pathname } = useLocation();
  const hasActiveChild = group.items.some(
    (item) => pathname === item.href || pathname.startsWith(`${item.href}/`),
  );
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const open = isOpen ?? hasActiveChild;
  const listId = `nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(!open)}
        aria-expanded={open}
        aria-controls={listId}
        className={cn(
          'flex w-full items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-sm transition-colors',
          hasActiveChild && !open
            ? 'bg-[var(--color-primary-subtle)] font-medium text-[var(--color-primary-on-subtle)]'
            : 'text-[var(--color-muted-foreground)] hover:bg-[var(--color-accent)] hover:text-[var(--color-foreground)]',
        )}
      >
        <group.icon className="size-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <ChevronDown
          className={cn('size-4 shrink-0 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      <ul id={listId} className="mt-0.5 space-y-0.5" hidden={!open}>
        {group.items.map((item) => (
          <li key={item.href}>
            <NavRow item={item} nested onNavigate={onNavigate} />
          </li>
        ))}
      </ul>
    </>
  );
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const hasRole = useAuthStore((state) => state.hasRole);
  const tenantName = useAuthStore((state) => state.user?.tenantName);
  const tenantSlug = useAuthStore((state) => state.user?.tenantSlug);

  // Logo SIPAUD, bukan logo lembaga. Kepala sidebar memang menamai
  // platformnya ("SIPAUD"), sedangkan nama lembaga ada pada baris di
  // bawahnya — lambang di sebelahnya harus mengikuti yang pertama.
  const { logoUrl: platformLogoUrl } = usePlatformBranding();

  // Super Admin tidak punya tenant sendiri — menu operasional tenant
  // (Master Data, Akademik, Keuangan, dst) tidak relevan untuknya, jadi ia
  // dapat menu konsol platform yang sepenuhnya terpisah, bukan gabungan.
  // Bukan lagi "apakah Super Admin", melainkan "apakah orang ini berdiri di
  // atas platform". Direktur dan Marketing memakai menu yang sama; butir yang
  // tidak boleh mereka buka disaring `permission` di tiap butirnya.
  const isPlatformUser = hasRole(...PLATFORM_ROLES);
  const menu = isPlatformUser ? PLATFORM_NAVIGATION : NAVIGATION;

  // Pendaftaran mandiri dari sipaud.com yang menunggu keputusan. Hook-nya
  // sendiri yang menahan diri untuk selain Super Admin.
  const pendingRegistrations = usePendingRegistrationCount();
  const pendingConfirmations = usePendingSubscriptionConfirmationCount();

  const isItemVisible = useNavItemVisibility();

  // Seksi yang seluruh itemnya tidak diizinkan ikut hilang, sehingga
  // tidak menyisakan judul kategori yang kosong. Anak menu disaring dengan
  // aturan yang sama, dan induk yang seluruh anaknya tersaring ikut hilang —
  // bukan tersisa sebagai pembuka daftar kosong.
  const sections = menu
    .map((section) => ({
      ...section,
      items: section.items
        .map((entry): NavEntry | null => {
          if (!isNavGroup(entry)) {
            return isItemVisible(entry) ? entry : null;
          }

          const items = entry.items.filter(isItemVisible);
          return items.length > 0 ? { ...entry, items } : null;
        })
        .filter((entry): entry is NavEntry => entry !== null),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      {/* Lapisan gelap saat sidebar dibuka di perangkat kecil. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-[var(--color-border)] bg-[var(--color-card)]',
          'transition-transform duration-200 ease-out lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* --- Kepala --- */}
        <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-[var(--color-border)] px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-md)] bg-[var(--color-primary)]">
              {platformLogoUrl ? (
                <img src={platformLogoUrl} alt="" className="size-full object-contain" />
              ) : (
                <GraduationCap
                  className="size-5 text-[var(--color-primary-foreground)]"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold leading-tight">SIPAUD</p>
              <p className="truncate text-xs text-[var(--color-muted-foreground)]">
                {tenantName ?? (isPlatformUser ? 'Konsol Platform' : 'Dashboard')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-[var(--radius-sm)] p-1.5 text-[var(--color-muted-foreground)] hover:bg-[var(--color-accent)] lg:hidden"
            aria-label="Tutup menu"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        {/* --- Menu ---
            Label melekat pada <nav>, bukan pada <aside> pembungkusnya:
            <aside> berperan sebagai landmark `complementary`, sehingga
            label di sana tidak menamai landmark navigasi mana pun. */}
        <nav
          aria-label="Navigasi utama"
          className="scrollbar-thin flex-1 overflow-y-auto px-3 py-4"
        >
          {sections.map((section) => (
            <div key={section.label} className="mb-6 last:mb-0">
              <h2 className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">
                {section.label}
              </h2>

              <ul className="space-y-0.5">
                {section.items.map((entry) => (
                  <li key={isNavGroup(entry) ? entry.label : entry.href}>
                    {isNavGroup(entry) ? (
                      <NavGroupRow group={entry} onNavigate={onClose} />
                    ) : (
                      <NavRow
                        item={entry}
                        onNavigate={onClose}
                        badge={
                          entry.href === PLATFORM_TENANTS_HREF
                            ? pendingRegistrations
                            : entry.href === PLATFORM_SUBSCRIPTION_CONFIRMATIONS_HREF
                              ? pendingConfirmations
                              : undefined
                        }
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Peran platform tidak punya lembaga sendiri — tidak ada portal wali
            yang jadi miliknya untuk dibagikan. */}
        {!isPlatformUser && tenantSlug && <PortalLinkFooter slug={tenantSlug} />}
      </aside>
    </>
  );
}
